import ApiError, { StandardErrorResponse } from '@/types/api';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios';
import { API_ENDPOINTS } from '@constants/api';

type AttachOptions = {
  tokenManager: {
    getAccessToken: () => string | null;
    getRefreshToken: () => string | null;
    setTokens: (t: { accessToken: string }) => void;
    clearTokens: () => void;
  };
  onLogout?: () => void; // opcional: para disparar logout global
};

type InternalConfig = AxiosRequestConfig & {
  _authRetry?: boolean;
  _retryCount?: number;
  skipAuthRefresh?: boolean; // para saltar el flujo de refresh en ciertas requests
};

// ---- Estado interno (encapsulado) ----
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string | null) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (token: string | null, error: unknown) => {
  failedQueue.forEach(({ resolve, reject }) =>
    token ? resolve(token) : reject(error)
  );
  failedQueue = [];
};

// ---- Utils ----
const getHeaderCI = (
  headers: AxiosRequestHeaders | Record<string, unknown> | undefined,
  name: string
): string | undefined => {
  if (!headers) return undefined;
  const key = Object.keys(headers).find(
    (k) => k.toLowerCase() === name.toLowerCase()
  );
  if (!key) return undefined;
  const value = (headers as Record<string, unknown>)[key];
  if (Array.isArray(value)) return String(value[0]);
  return typeof value === 'string' ? value : String(value);
};

const saveRequestId = (
  headers?: AxiosRequestHeaders | Record<string, unknown>
) => {
  const rid = getHeaderCI(headers, 'x-request-id');
  if (!rid) return;
  try {
    sessionStorage.setItem('X-Request-ID', rid);
  } catch {
    /* ignore */
  }
};

const buildApiError = (
  err: unknown,
  fallback: Partial<StandardErrorResponse> = {}
): ApiError => {
  const isAxiosErr = axios.isAxiosError(err);
  const error = isAxiosErr ? (err as AxiosError<StandardErrorResponse>) : null;
  const resp = error?.response;
  const respData = resp?.data as StandardErrorResponse | undefined | null;

  const requestId =
    respData?.requestId ||
    getHeaderCI(resp?.headers, 'x-request-id') ||
    (() => {
      try {
        return sessionStorage.getItem('X-Request-ID');
      } catch {
        return null;
      }
    })() ||
    null;

  if (respData && typeof respData === 'object' && 'code' in respData) {
    return new ApiError({
      status: respData.status ?? resp?.status ?? 500,
      code: respData.code || 'COMMON.INTERNAL_ERROR',
      message: respData.message || 'Error',
      details: respData.details ?? null,
      timestamp: respData.timestamp,
      path: respData.path,
      requestId,
    });
  }

  const commonMessage = isAxiosErr
    ? error?.code === 'ECONNABORTED'
      ? 'Request timeout'
      : error?.message || 'Network error'
    : String(err ?? 'Unexpected error');

  const isNetworkError = isAxiosErr && error?.code === 'ERR_NETWORK';

  return new ApiError({
    status: resp?.status ?? 0,
    code: isNetworkError ? 'COMMON.NETWORK_ERROR' : 'COMMON.INTERNAL_ERROR',
    message: commonMessage,
    details: null,
    timestamp: new Date().toISOString(),
    path: (error?.config as AxiosRequestConfig)?.url,
    requestId,
    ...fallback,
  });
};

// ---- Punto de entrada público ----
export const attachInterceptors = (
  api: AxiosInstance,
  { tokenManager, onLogout }: AttachOptions
) => {
  // REQUEST
  api.interceptors.request.use(
    (config) => {
      // Authorization
      const token = tokenManager.getAccessToken();
      if (token) {
        const headers = (config.headers ?? {}) as AxiosRequestHeaders;
        headers.Authorization = `Bearer ${token}`;
        config.headers = headers;
      }

      // Propagar X-Request-ID si lo tenemos en sessionStorage
      try {
        const requestId = sessionStorage.getItem('X-Request-ID');
        if (requestId) {
          const headers = (config.headers ?? {}) as AxiosRequestHeaders;
          headers['X-Request-ID'] = requestId;
          config.headers = headers;
        }
      } catch {
        /* ignore */
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // RESPONSE
  api.interceptors.response.use(
    // Éxito: guarda X-Request-ID si vino
    (response) => {
      saveRequestId(response.headers);
      return response;
    },
    // Error: manejo de refresh + normalización de errores
    async (error: AxiosError<StandardErrorResponse>) => {
      const originalRequest = error.config as InternalConfig;

      // Guarda X-Request-ID también en respuestas de error
      saveRequestId(error.response?.headers);

      // Si no hay response -> red/timeout/cancel
      if (!error.response) {
        return Promise.reject(buildApiError(error));
      }

      const status = error.response.status;
      const url = originalRequest?.url || '';
      const isAuthEndpoint =
        url.endsWith(API_ENDPOINTS.AUTH.REFRESH) ||
        url.endsWith(API_ENDPOINTS.AUTH.LOGIN);

      // Auto refresh 401
      if (!isAuthEndpoint && !originalRequest?.skipAuthRefresh && status === 401) {
        if (isRefreshing) {
          // Esperar a que termine el refresh y reinyectar token
          return new Promise<string | null>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (!token) throw new Error('No access token after refresh');
              const headers =
                (originalRequest.headers ?? {}) as AxiosRequestHeaders;
              headers.Authorization = `Bearer ${token}`;
              originalRequest.headers = headers;
              return api.request(originalRequest);
            })
            .catch((err) => Promise.reject(buildApiError(err)));
        }

        if (originalRequest._authRetry) {
          // Evita bucles
          return Promise.reject(buildApiError(error));
        }

        originalRequest._authRetry = true;
        originalRequest._retryCount = (originalRequest._retryCount ?? 0) + 1;
        isRefreshing = true;

        try {
          const refreshToken = tokenManager.getRefreshToken();
          if (!refreshToken) throw new Error('No refresh token available');

          const res = await api.post(API_ENDPOINTS.AUTH.REFRESH, { refreshToken });
          const data = (res.data as { accessToken?: string } | undefined) || {};
          const accessToken = data.accessToken;

          if (!accessToken) throw new Error('No access token in refresh response');

          tokenManager.setTokens({ accessToken });

          // Despertar en cola
          processQueue(accessToken, null);

          // Reintentar original
          const headers = (originalRequest.headers ?? {}) as AxiosRequestHeaders;
          headers.Authorization = `Bearer ${accessToken}`;
          originalRequest.headers = headers;

          return api.request(originalRequest);
        } catch (refreshErr: unknown) {
          // Notificar logout si se proporciona
          if (onLogout) {
            try {
              onLogout();
            } catch {
              /* ignore */
            }
          } else {
            // fallback al evento que ya usabas
            try {
              window.dispatchEvent(new CustomEvent('auth:logged-out'));
            } catch {/* ignore */ }
          }

          tokenManager.clearTokens();
          processQueue(null, refreshErr);
          return Promise.reject(buildApiError(refreshErr));
        } finally {
          isRefreshing = false;
        }
      }

      // Envolver cualesquiera otros errores en ApiError
      return Promise.reject(buildApiError(error));
    }
  );
};
